import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, Lock, User, Mail, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function LoginPage() {
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoSeeding, setIsAutoSeeding] = useState(false);
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ 
    email: "", 
    password: "", 
    fullName: "", 
    role: "driver" 
  });
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    const checkAndSeed = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/status`);
        const data = await res.json();
        if (!data.initialized) {
          setIsAutoSeeding(true);
          toast.info("Initializing demo data...");
          const seedRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed`, { method: 'POST' });
          const seedData = await seedRes.json();
          if (seedData.admin_email) {
            setLoginData({ email: seedData.admin_email, password: seedData.admin_password });
            toast.success("Demo data loaded. Credentials pre-filled.");
          }
          setIsAutoSeeding(false);
        }
      } catch {
        setIsAutoSeeding(false);
      }
    };
    checkAndSeed();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Access granted. Welcome to Fleet Command.");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Access denied. Invalid credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.fullName, registerData.role);
      toast.success("Registration complete. Welcome to Fleet Command.");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed. Try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const seedDemoData = async () => {
    setIsLoading(true);
    try {
      // First try seeding directly
      let response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed`, {
        method: 'POST'
      });
      let data = await response.json();

      // If data already exists, clear and re-seed
      if (data.message === "Data already seeded") {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clear-data`, {
          method: 'POST'
        });
        response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/seed`, {
          method: 'POST'
        });
        data = await response.json();
      }

      if (data.admin_email) {
        setLoginData({ email: data.admin_email, password: data.admin_password });
        toast.success("Demo data loaded. Use admin credentials to login.");
      } else {
        toast.info(data.message || "Demo data operation completed.");
      }
    } catch (error) {
      toast.error("Failed to seed demo data");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background tactical-grid flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/3 rounded-full blur-3xl" />
      </div>

      <div className="w-full max-w-md relative z-10">
        {/* Logo/Header */}
        <div className="text-center mb-8 animate-slide-in">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-sm bg-card border border-border mb-4 relative hud-corners">
            <Shield className="w-10 h-10 text-primary glow-green" />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-foreground">
            TANZANIA DEFENSE
          </h1>
          <p className="text-primary font-heading text-xl tracking-wider">
            FLEET COMMAND
          </p>
          <p className="text-muted-foreground text-sm mt-2 font-mono">
            AUTHORIZED PERSONNEL ONLY
          </p>
        </div>

        <Card className="bg-card/90 backdrop-blur-md border-border rounded-sm animate-slide-in stagger-2">
          <CardHeader className="border-b border-border pb-4">
            <div className="flex items-center gap-2">
              <Lock className="w-4 h-4 text-primary" />
              <CardTitle className="font-heading text-lg tracking-wide">SECURITY CLEARANCE</CardTitle>
            </div>
            <CardDescription className="font-mono text-xs text-muted-foreground">
              Enter credentials to access the system
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6">
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 bg-muted rounded-sm mb-6">
                <TabsTrigger 
                  value="login" 
                  className="rounded-sm font-heading tracking-wide data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  LOGIN
                </TabsTrigger>
                <TabsTrigger 
                  value="register"
                  className="rounded-sm font-heading tracking-wide data-[state=active]:bg-primary data-[state=active]:text-primary-foreground"
                >
                  REGISTER
                </TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div className="space-y-2">
                    <Label htmlFor="login-email" className="text-xs font-mono text-muted-foreground uppercase">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="operator@tdfc.go.tz"
                        value={loginData.email}
                        onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                        className="pl-10 bg-input border-border rounded-sm font-mono text-sm focus:ring-1 focus:ring-primary"
                        required
                        data-testid="login-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password" className="text-xs font-mono text-muted-foreground uppercase">
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                        className="pl-10 bg-input border-border rounded-sm font-mono text-sm focus:ring-1 focus:ring-primary"
                        required
                        data-testid="login-password-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider uppercase btn-tactical"
                    disabled={isLoading}
                    data-testid="login-submit-btn"
                  >
                    {isLoading ? "AUTHENTICATING..." : "ACCESS SYSTEM"}
                  </Button>
                </form>

                <div className="mt-6 pt-6 border-t border-border">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={seedDemoData}
                    className="w-full border-primary/50 text-primary hover:bg-primary/10 rounded-sm font-mono text-xs"
                    disabled={isLoading}
                    data-testid="seed-demo-btn"
                  >
                    <AlertTriangle className="w-3 h-3 mr-2" />
                    LOAD DEMO DATA
                  </Button>
                  <p className="text-center text-muted-foreground text-xs mt-2 font-mono">
                    Demo: admin@tdfc.go.tz / admin123
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                  <div className="space-y-2">
                    <Label htmlFor="register-name" className="text-xs font-mono text-muted-foreground uppercase">
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="Enter your full name"
                        value={registerData.fullName}
                        onChange={(e) => setRegisterData({ ...registerData, fullName: e.target.value })}
                        className="pl-10 bg-input border-border rounded-sm font-mono text-sm focus:ring-1 focus:ring-primary"
                        required
                        data-testid="register-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email" className="text-xs font-mono text-muted-foreground uppercase">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="your.email@tdfc.go.tz"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                        className="pl-10 bg-input border-border rounded-sm font-mono text-sm focus:ring-1 focus:ring-primary"
                        required
                        data-testid="register-email-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password" className="text-xs font-mono text-muted-foreground uppercase">
                      Password
                    </Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type="password"
                        placeholder="••••••••"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                        className="pl-10 bg-input border-border rounded-sm font-mono text-sm focus:ring-1 focus:ring-primary"
                        required
                        data-testid="register-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-mono text-muted-foreground uppercase">
                      Role
                    </Label>
                    <Select 
                      value={registerData.role} 
                      onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                    >
                      <SelectTrigger className="bg-input border-border rounded-sm font-mono text-sm" data-testid="register-role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border rounded-sm">
                        <SelectItem value="driver" className="font-mono text-sm">Driver</SelectItem>
                        <SelectItem value="fleet_manager" className="font-mono text-sm">Fleet Manager</SelectItem>
                        <SelectItem value="admin" className="font-mono text-sm">Administrator</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm font-heading tracking-wider uppercase btn-tactical"
                    disabled={isLoading}
                    data-testid="register-submit-btn"
                  >
                    {isLoading ? "PROCESSING..." : "CREATE ACCOUNT"}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        <p className="text-center text-muted-foreground text-xs mt-6 font-mono animate-slide-in stagger-3">
          DEPARTMENT OF DEFENSE AND NATIONAL SERVICE
          <br />
          UNITED REPUBLIC OF TANZANIA
          <br />
          <span className="text-[10px] opacity-60 mt-1 inline-block">Powered by Akendra Pty Ltd</span>
        </p>
      </div>
    </div>
  );
}
